import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";
import type { Plan } from "@headerhawk/core";
import { getSessionToken, getTokenClaims } from "@/lib/auth/token-manager";

const PERSIST_KEY = "onboarding-state";
const ANONYMOUS_SCOPE = "anonymous";

/**
 * Onboarding progress belongs to one account, not one browser. Without a scope
 * a second person signing in on a shared machine inherits whatever the first
 * person had completed - and, if their own account has no sites yet, lands in
 * a dashboard that immediately bounces them back out.
 */
function currentScope(): string {
  const token = getSessionToken();
  const sub = token ? getTokenClaims(token)?.sub : undefined;
  return sub ?? ANONYMOUS_SCOPE;
}

export function onboardingStorageKey(scope: string = currentScope()): string {
  return `${PERSIST_KEY}:${scope}`;
}

/**
 * Adopt progress written under the old un-namespaced key, once.
 *
 * Without this, everyone mid-signup at deploy time loses `createdSiteId` and is
 * walked back to a blank site form - where re-entering the domain they already
 * created 409s with no way forward. The legacy key is deleted as soon as it is
 * read, so the window in which a different account could inherit it is a single
 * page load, and FE-H1's server-authoritative rule means stale steps can no
 * longer produce a redirect loop regardless.
 */
function adoptLegacyState(name: string, scope: string): string | null {
  if (scope === ANONYMOUS_SCOPE) return null;

  const legacy = localStorage.getItem(name);
  if (legacy === null) return null;

  localStorage.removeItem(name);
  localStorage.setItem(`${name}:${scope}`, legacy);
  return legacy;
}

const userScopedStorage: StateStorage = {
  getItem: (name) => {
    const scope = currentScope();
    return (
      localStorage.getItem(`${name}:${scope}`) ?? adoptLegacyState(name, scope)
    );
  },
  setItem: (name, value) =>
    localStorage.setItem(`${name}:${currentScope()}`, value),
  removeItem: (name) => localStorage.removeItem(`${name}:${currentScope()}`),
};

export type OnboardingStep = "org" | "tier" | "site" | "setup";

const STEP_ORDER: OnboardingStep[] = ["org", "tier", "site", "setup"];

interface OnboardingState {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  selectedTier: Plan | null;
  createdSiteId: string | null;

  // Actions
  setStep: (step: OnboardingStep) => void;
  completeStep: (step: OnboardingStep) => void;
  setSelectedTier: (tier: Plan) => void;
  setCreatedSiteId: (siteId: string) => void;
  reset: () => void;
  isStepCompleted: (step: OnboardingStep) => boolean;
  canAccessStep: (step: OnboardingStep) => boolean;
  getNextStep: (currentStep: OnboardingStep) => OnboardingStep | null;
  getPreviousStep: (currentStep: OnboardingStep) => OnboardingStep | null;
}

type OnboardingData = Pick<
  OnboardingState,
  "currentStep" | "completedSteps" | "selectedTier" | "createdSiteId"
>;

const INITIAL_STATE: OnboardingData = {
  currentStep: "org",
  completedSteps: [],
  selectedTier: null,
  createdSiteId: null,
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setStep: (step) => set({ currentStep: step }),

      completeStep: (step) => {
        const { completedSteps } = get();
        if (!completedSteps.includes(step)) {
          set({ completedSteps: [...completedSteps, step] });
        }
      },

      setSelectedTier: (tier) => set({ selectedTier: tier }),
      setCreatedSiteId: (siteId) => set({ createdSiteId: siteId }),

      reset: () => set({ ...INITIAL_STATE }),

      isStepCompleted: (step) => get().completedSteps.includes(step),

      canAccessStep: (step) => {
        const { completedSteps } = get();
        const stepIndex = STEP_ORDER.indexOf(step);
        if (stepIndex === 0) return true;
        if (stepIndex === -1) return false;
        const previousStep = STEP_ORDER[stepIndex - 1];
        if (!previousStep) return false;
        return completedSteps.includes(previousStep);
      },

      getNextStep: (currentStep) => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex === -1 || currentIndex >= STEP_ORDER.length - 1) {
          return null;
        }
        return STEP_ORDER[currentIndex + 1] ?? null;
      },

      getPreviousStep: (currentStep) => {
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex <= 0) {
          return null;
        }
        return STEP_ORDER[currentIndex - 1] ?? null;
      },
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() => userScopedStorage),
    },
  ),
);

/**
 * Re-read persisted state for whoever is signed in now. Zustand hydrates once
 * at module load, so a same-tab account switch would otherwise keep reading the
 * previous account's slot.
 */
export function rehydrateOnboardingStore(): void {
  if (localStorage.getItem(onboardingStorageKey()) === null) {
    // `persist.rehydrate()` merges over the current state, so with nothing
    // stored for this account it would leave the previous account's progress
    // in memory - exactly the leak the namespaced key exists to prevent.
    useOnboardingStore.setState({ ...INITIAL_STATE });
    return;
  }
  void useOnboardingStore.persist.rehydrate();
}

export { STEP_ORDER };
